// src/components/HealthBadge.jsx
//
// Compact pill that pairs a HealthDot with the canonical HEALTH label
// ("On Track" / "At Risk" / "Critical"). Encapsulates the dot+label
// structure that was previously inlined inside LocationDetail's
// identity bar (`data-testid="health-badge"` wrapper). The testid is
// preserved on the outer element so existing assertions (vitest +
// playwright) resolve unchanged.
//
// LP1.2 Gate 1 — opt-in inline editing follows the StageBadge pattern.
//   - Display-only when `canWrite` is omitted/false (default).
//   - Editable when `canWrite` is true AND `onChange` is provided:
//     click opens a dropdown of the 3 HEALTH options; selecting an
//     option calls `await onChange(newKey)`. Busy + inline-error
//     handling matches StageBadge.
//   - A11y: Escape closes + returns focus, ArrowUp/Down navigate
//     options, click-outside closes.
//
// LP1.3 Gate 1 — pill-shape conversion. Mirrors StageBadge's form
// factor exactly (S.badge + per-health bg/color from HEALTH enum).
// The HealthDot is removed from this surface; the dot still lives on
// in HealthDot.jsx for callers that need the bare dot (e.g. health-
// distribution legends). Editable path keeps the ▾ caret + dropdown
// menu (which still uses HealthDot inside the option rows for visual
// parity with the StageBadge color-swatch). The `dotSize` prop is
// retained on the menu options only.

import React, { useEffect, useId, useRef, useState } from "react";
import { HEALTH, T } from "../app/tokens.js";
import { S } from "../app/styles.js";
import { HealthDot } from "./HealthDot.jsx";

const HEALTH_KEYS = Object.keys(HEALTH);
// Fallback for unknown/missing health values — use a neutral grey pill
// so the surface still renders something meaningful instead of blank.
const NOT_SET     = { label: "Not Set", bg: T.bg, color: T.mid };

export function HealthBadge({ health, canWrite = false, onChange }) {
  const info     = HEALTH[health] || NOT_SET;
  const editable = canWrite && typeof onChange === "function";

  if (!editable) {
    // Canonical squarish pill shape (borderRadius 6, no border, semibold weight).
    return (
      <div
        data-testid="health-badge"
        aria-label={`Health: ${info.label}`}
        style={{
          display:      "inline-flex",
          alignItems:   "center",
          borderRadius: 6,
          padding:      "2px 10px",
          fontSize:     T.fsXs,
          fontWeight:   T.fwSemibold,
          background:   info.bg,
          color:        info.color,
          whiteSpace:   "nowrap",
        }}
      >
        {info.label}
      </div>
    );
  }

  return <EditableHealthBadge health={health} info={info} onChange={onChange} />;
}

function EditableHealthBadge({ health, info, onChange }) {
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const triggerRef        = useRef(null);
  const menuRef           = useRef(null);
  const menuId            = useId();

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const active = menuRef.current?.querySelector(`[data-testid="health-badge-option-${health}"]`);
      (active || menuRef.current?.querySelector("button"))?.focus();
    });
  }, [open, health]);

  function onMenuKeyDown(e) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const buttons = Array.from(menuRef.current?.querySelectorAll("button[data-testid^='health-badge-option-']") || []);
    const idx = buttons.indexOf(document.activeElement);
    if (idx < 0) return;
    e.preventDefault();
    const next = e.key === "ArrowDown" ? (idx + 1) % buttons.length : (idx - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  async function handleSelect(newKey) {
    if (busy || newKey === health) {
      if (newKey === health) {
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onChange(newKey);
      setBusy(false);
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
    } catch (err) {
      setBusy(false);
      setError(mapError(err));
    }
  }

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="health-badge"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Health: ${info.label}`}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 6,
          padding: "2px 10px",
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          letterSpacing: 0.3,
          lineHeight: 1,
          whiteSpace: "nowrap",
          background: info.bg,
          color: info.color,
          border: "none",
          cursor: busy ? "wait" : "pointer",
          gap: 4,
          opacity: busy ? 0.6 : 1,
        }}
      >
        {info.label}
        <span aria-hidden="true" style={{ fontSize: T.fsXxxs, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          data-testid="health-badge-menu"
          onKeyDown={onMenuKeyDown}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            minWidth: 140,
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            boxShadow: T.cardShadow,
            padding: 4,
          }}
        >
          {HEALTH_KEYS.map((key) => {
            const opt    = HEALTH[key];
            const active = key === health;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={active}
                data-testid={`health-badge-option-${key}`}
                onClick={() => handleSelect(key)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "6px 10px",
                  background: active ? T.bg : "transparent",
                  border: "none",
                  borderRadius: 4,
                  cursor: busy ? "wait" : "pointer",
                  fontSize: T.fsSm,
                  fontWeight: active ? 600 : 500,
                  color: T.dark,
                  textAlign: "left",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <HealthDot health={key} size={10} />
                  {opt.label}
                </span>
                {active && <span aria-hidden="true" style={{ color: T.mid, fontSize: T.fsXs }}>✓</span>}
              </button>
            );
          })}
          {error && (
            <div
              data-testid="health-badge-error"
              role="alert"
              style={{ padding: "6px 10px", fontSize: T.fsXs, color: T.red }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function mapError(err) {
  const name = err?.name;
  if (name === "AuthError")       return "You don't have permission to change health.";
  if (name === "ValidationError") return err.message || "Invalid health.";
  if (name === "NotFoundError")   return "Location not found.";
  if (name === "NetworkError")    return "Network error. Please try again.";
  return "Couldn't update health. Please try again.";
}
